import axiosInstance from './axiosConfig';
import { handleApiError } from '../../utils/errorHandling';

const INVITE_CODE_PATTERN = /^[A-Za-z0-9]{1,32}$/;

/** 招待報酬の段階。登録時と初回バナー生成時で 500cr ずつ入る。 */
export type InviteRewardStage = 'signup' | 'activation';

/** まだ招待者に知らせていない付与。ホーム表示時にトーストで出す。 */
export interface UnnotifiedInviteReward {
  referral_id: number;
  invitee_name: string | null;
  stage: InviteRewardStage;
  amount: number;
}

export interface InviteSummary {
  /** 組織未所属（オンボ未完了）のユーザーでは発行されないため null になる。 */
  code: string | null;
  invite_url: string | null;
  expires_at: string | null;
  registered_count: number;
  activated_count: number;
  earned_credits: number;
  monthly_remaining: number;
  unnotified: UnnotifiedInviteReward[];
}

export interface InviteVerification {
  valid: boolean;
  inviter_name: string | null;
  expires_at: string | null;
}

/**
 * 招待リンクと実績を取得する。招待リンクは初回アクセス時にサーバー側で発行される。
 */
export const fetchInviteSummary = async (): Promise<InviteSummary> => {
  try {
    const response = await axiosInstance.get('/invite/summary');
    return response.data;
  } catch (error) {
    throw handleApiError(error, '招待情報の取得に失敗しました');
  }
};

/**
 * まだ知らせていない付与だけを取得する。
 *
 * ホーム表示のたびに呼ぶため、summary と違って招待リンクを発行しない
 * エンドポイントを使う。
 */
export const fetchPendingInviteRewards = async (): Promise<UnnotifiedInviteReward[]> => {
  const response = await axiosInstance.get('/invite/rewards/pending');
  return response.data?.unnotified ?? [];
};

/**
 * トースト表示済みの付与を既読にする。
 */
export const markInviteRewardsNotified = async (
  rewards: Array<{ referral_id: number; stage: InviteRewardStage }>,
): Promise<void> => {
  if (rewards.length === 0) {
    return;
  }

  try {
    await axiosInstance.post('/invite/rewards/mark-notified', { rewards });
  } catch (error) {
    throw handleApiError(error, '招待ボーナスの既読化に失敗しました');
  }
};

/**
 * 招待コードの有効性と招待者名を引く（未認証）。
 *
 * 失効・不存在でもサーバーは 200 + valid:false を返す。ネットワーク断など
 * 想定外の失敗でも signup を止めないよう、ここでは無効扱いにフォールバックする。
 */
export const verifyInviteCode = async (code: string): Promise<InviteVerification> => {
  // routes/api/invite.php の where('code', '[A-Za-z0-9]{1,32}') と同じ形式だけ問い合わせる
  if (!INVITE_CODE_PATTERN.test(code)) {
    return { valid: false, inviter_name: null, expires_at: null };
  }

  try {
    const response = await axiosInstance.get(`/invite/${encodeURIComponent(code)}/verify`);
    return response.data;
  } catch {
    return { valid: false, inviter_name: null, expires_at: null };
  }
};
